import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import type { WeeklyAnalyticsRow } from "../../types";
import type { useIssuesMeta } from "../issues/meta";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart";
import { shortWeek } from "./metrics";

export function CompletedByTypeChart({
  rows,
  meta,
}: {
  rows: WeeklyAnalyticsRow[];
  meta: ReturnType<typeof useIssuesMeta>["data"];
}) {
  const typeKeys = [...new Set(rows.filter((row) => row.completed > 0).map((row) => row.typeKey))];
  const config = Object.fromEntries(
    typeKeys.map((key, index) => [
      key,
      {
        label: meta?.types.find((t) => t.key === key)?.name ?? key,
        color: meta?.types.find((t) => t.key === key)?.color ?? `var(--chart-${(index % 5) + 1})`,
      },
    ]),
  ) satisfies ChartConfig;
  const byWeek = new Map<string, Record<string, number | string>>();
  for (const row of rows) {
    const entry = byWeek.get(row.weekStart) ?? { week: shortWeek(row.weekStart) };
    entry[row.typeKey] = ((entry[row.typeKey] as number) ?? 0) + row.completed;
    byWeek.set(row.weekStart, entry);
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Completed by type</CardTitle>
        <CardDescription>What shipped each week, split by issue type</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={config} className="aspect-auto h-64 w-full">
          <BarChart data={[...byWeek.values()]} margin={{ left: -20, right: 4 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="week" tickLine={false} axisLine={false} tickMargin={8} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            {typeKeys.map((key) => (
              <Bar key={key} dataKey={key} stackId="types" fill={`var(--color-${key})`} />
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
